'use client';

import { useEffect, useId, useRef, useState } from 'react';
import {
  filterCourtLocationSuggestions,
  type CourtLocationSuggestion,
} from '@/lib/smart-finder/court-location-suggestions';

interface CourtLocationTypeaheadProps {
  /** Called with the canonical search text when the visitor picks or clears. */
  onSelect: (searchText: string | null) => void;
  placeholder?: string;
  /** Characters typed before suggestions appear. */
  minChars?: number;
  autoFocus?: boolean;
}

/**
 * Google-style court/city suggestions for nationwide search surfaces. The
 * visitor picks a court and the canonical text rides along with their query —
 * so the server resolves the scope instead of bouncing them for a location.
 */
export default function CourtLocationTypeahead({
  onSelect,
  placeholder = 'City or court — e.g. Ludhiana, Bombay High Court',
  minChars = 2,
  autoFocus = false,
}: CourtLocationTypeaheadProps) {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<CourtLocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [chosen, setChosen] = useState<CourtLocationSuggestion | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useEffect(() => {
    const onOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('touchstart', onOutsideClick);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('touchstart', onOutsideClick);
    };
  }, []);

  const update = (next: string) => {
    setValue(next);
    if (chosen) {
      setChosen(null);
      onSelect(null);
    }
    const matched = next.trim().length >= minChars ? filterCourtLocationSuggestions(next) : [];
    setSuggestions(matched);
    setHighlighted(matched.length > 0 ? 0 : -1);
    setOpen(matched.length > 0);
  };

  const choose = (suggestion: CourtLocationSuggestion) => {
    setChosen(suggestion);
    setValue(suggestion.display);
    setOpen(false);
    onSelect(suggestion.searchText);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted(prev => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      if (highlighted >= 0) {
        event.preventDefault();
        choose(suggestions[highlighted]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <label className="block">
        <span className="sr-only">Court or city</span>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && highlighted >= 0 ? `${listboxId}-${highlighted}` : undefined}
          value={value}
          onChange={(event) => update(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { if (suggestions.length > 0 && !chosen) setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
          autoFocus={autoFocus}
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
        />
      </label>
      {open && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label="Court and city suggestions"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.searchText}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={index === highlighted}
              onMouseDown={(event) => { event.preventDefault(); choose(suggestion); }}
              onMouseEnter={() => setHighlighted(index)}
              className={`cursor-pointer px-3 py-2.5 text-sm ${index === highlighted ? 'bg-primary-50 text-primary-900' : 'text-gray-800'}`}
            >
              {suggestion.display}
            </li>
          ))}
        </ul>
      )}
      {chosen && (
        <p className="mt-1.5 text-xs font-medium text-primary-800" role="status">
          Searching {chosen.display}.
        </p>
      )}
    </div>
  );
}
